import { useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DemoModal({ open, onOpenChange }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Nome e e-mail são obrigatórios");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/demo-request`, form);
      setSuccess(true);
      toast.success("Pedido enviado! Entramos em contacto em breve.");
    } catch (err) {
      console.error(err);
      toast.error("Falha ao enviar. Tente novamente em instantes.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (v) => {
    onOpenChange(v);
    if (!v) {
      setTimeout(() => {
        setSuccess(false);
        setForm({ name: "", email: "", phone: "", company: "", message: "" });
      }, 200);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="demo-modal"
        className="max-w-lg bg-slate-950/95 backdrop-blur-xl border-white/10 text-slate-100"
      >
        {success ? (
          <div data-testid="demo-success" className="text-center py-8">
            <div className="mx-auto h-14 w-14 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <CheckCircle2 className="h-7 w-7 text-emerald-400" />
            </div>
            <h3 className="mt-6 text-2xl font-medium text-white tracking-tight">
              Pedido recebido.
            </h3>
            <p className="mt-3 text-slate-400 max-w-sm mx-auto leading-relaxed">
              Obrigado, {form.name.split(" ")[0]}. A nossa equipa entra em contacto consigo
              em menos de 24h para activar o seu período de teste.
            </p>
            <Button
              data-testid="demo-success-close"
              onClick={() => handleOpenChange(false)}
              className="mt-6 btn-primary-glow border-0 rounded-full px-6"
            >
              Fechar
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-medium tracking-tight text-white">
                Experimente 7 dias grátis
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Sem cartão de crédito. Sem compromisso. Conte-nos sobre o seu negócio e activamos a sua conta hoje.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-4 mt-2">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Nome" required>
                  <Input
                    data-testid="demo-input-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Maria Silva"
                    className="bg-white/[0.04] border-white/10 text-white placeholder:text-slate-500"
                  />
                </Field>
                <Field label="E-mail" required>
                  <Input
                    data-testid="demo-input-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="maria@empresa.pt"
                    className="bg-white/[0.04] border-white/10 text-white placeholder:text-slate-500"
                  />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Telemóvel">
                  <Input
                    data-testid="demo-input-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+351 912 345 678"
                    className="bg-white/[0.04] border-white/10 text-white placeholder:text-slate-500"
                  />
                </Field>
                <Field label="Empresa">
                  <Input
                    data-testid="demo-input-company"
                    value={form.company}
                    onChange={update("company")}
                    placeholder="Clínica Exemplo"
                    className="bg-white/[0.04] border-white/10 text-white placeholder:text-slate-500"
                  />
                </Field>
              </div>
              <Field label="Conte-nos sobre o seu negócio">
                <Textarea
                  data-testid="demo-input-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={3}
                  placeholder="Faço marcações de... e gostaria de automatizar..."
                  className="bg-white/[0.04] border-white/10 text-white placeholder:text-slate-500 resize-none"
                />
              </Field>

              <Button
                data-testid="demo-submit"
                type="submit"
                disabled={loading}
                className="w-full btn-primary-glow text-white border-0 rounded-full h-12 font-medium mt-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    A enviar...
                  </>
                ) : (
                  "Experimente 7 dias grátis"
                )}
              </Button>
              {loading && (
                <p className="text-[12px] text-slate-400 text-center animate-pulse">
                  Aguarde um momento, estamos a processar o seu pedido...
                </p>
              )}
              <p className="text-[11px] text-slate-500 text-center">
                Ao enviar, concorda em ser contactado pela equipa Automia.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.15em] text-slate-400 mb-2 block">
        {label} {required && <span className="text-blue-400">*</span>}
      </Label>
      {children}
    </div>
  );
}
